import { Switch, useHistory, useRouteMatch } from "react-router-dom";
import AdminLayout from "../layouts/AdminLayout";
import { SecureRoute, useOktaAuth } from "@okta/okta-react";
import TestOrdersPageRoutes from "../pages/TestOrdersTablePage";
import ShipmentsPage from "../pages/ShipmentsPage";
import OrderPageRoutes from "../pages/OnlineOrder/OrderPageRoutes";
import ProfilePageRoutes from "./ProfilePageRoutes";
import NotificationsPage from "../pages/Notifications/NotificationsPage";
import Dashboard from "../pages/DashboardPage";
import { useEffect, useState, Suspense, lazy } from "react";
import ApprovalsReview from "../pages/ApprovalsReview";
import useSyncAssociations from "../services/utils/custom hooks/Auth/useSyncAssociations";
import PatientsPageRoutes from "./PatientsPageRoutes";
import OrderAuthorizedPage from "../pages/OrderAuthorizedPage";
import { SelectedRowIDSProvider } from "../services/context/selectedRowsIDContext";
import SchedulePickup from "../pages/SchedulePickup";
import Tour from "../components/global/Tour";
import { useAdminContext } from "../services/context/TourPopupContext";
import NewReleasePopup from "../components/atomic/NewReleasePopup";
import useProfile from "../services/utils/custom hooks/useProfile";
import TermsAndConditionsPopup from "../components/popups/termsAndConditionPopup";
import CustomPopUp from "../components/global/CustomPopup";
import ApprovalsReviewMobile from "../pages/ApprovalsReviewMobile";
import useMobile from "../utils/helpers/useMobile";
import TableLoadingFallback from "../components/global/LoadingIndicators/TableLoadingFallback";
import NotFoundPage from "../pages/NotFoundPage";
import { datadogRum } from "@datadog/browser-rum";

// Lazy load heavy components and non-critical routes
const SettingsPage = lazy(() => import("../pages/SettingsPage"));
const Help = lazy(() => import("../pages/HelpPage"));
const DocumentsPage = lazy(() => import("../pages/DocumentsPage"));
const ApprovalsTable = lazy(() => import("../components/Tables/ApprovalsTable"));
const ApprovalsTableMobile = lazy(() => import("../components/Tables/ApprovalsTableMobile"));

// Named components for Suspense-wrapped routes to prevent remounting issues
const SettingsPageWithSuspense = () => (
  <Suspense fallback={<TableLoadingFallback />}>
    <SettingsPage />
  </Suspense>
);

const HelpPageWithSuspense = () => (
  <Suspense fallback={<TableLoadingFallback />}>
    <Help />
  </Suspense>
);

const DocumentsPageWithSuspense = () => (
  <Suspense fallback={<TableLoadingFallback />}>
    <DocumentsPage />
  </Suspense>
);

interface PopupWrapperProps {
  type: "tour" | "release";
  isActive: boolean;
  onClose: () => void;
  startTour?: () => void;
  isReleasePopupButtonEnabled?: boolean;
}

function AdminLandingPage(): JSX.Element | null {
  const history = useHistory();

  useEffect(() => {
    history.push(`/access/dashboard`);
  }, []);
  return null;
}

function ShipmentsRoute() {
  return (
    <SelectedRowIDSProvider>
      <ShipmentsPage />
    </SelectedRowIDSProvider>
  );
}
function ApprovalsRouteDesktop() {
  return (
    <SelectedRowIDSProvider>
      <Suspense fallback={<TableLoadingFallback />}>
        <ApprovalsTable />
      </Suspense>
    </SelectedRowIDSProvider>
  )
}
function PopupWrapper({
  type,
  isActive,
  onClose,
  startTour,
  isReleasePopupButtonEnabled
}: PopupWrapperProps): JSX.Element | null {

  if (!isActive) return null;

  if (type === "tour") {
    return <Tour onClose={onClose} />;
  }

  if (type === "release") {
    return (
      <NewReleasePopup
        startTour={startTour!}
        onClose={onClose}
        isReleasePopupButtonEnabled={isReleasePopupButtonEnabled}
      />
    );
  }

  return null;
}


function ApprovalsTableResponsive(): JSX.Element {
  const isMobile = useMobile()
  return (
    <Suspense fallback={<TableLoadingFallback />}>
      {isMobile ? <ApprovalsTableMobile /> : <ApprovalsRouteDesktop />}
    </Suspense>
  )
}

function ApprovalsReviewResponsive(): JSX.Element {
  const isMobile = useMobile()
  return (
    <>
      {isMobile ? <ApprovalsReviewMobile /> : <ApprovalsReview />}
    </>
  );
}


function AdminRoutes(): JSX.Element {
  const match = useRouteMatch();
  useSyncAssociations();
  const oktaAuth: any = useOktaAuth();
  const { data: profile, isLoading } = useProfile();
  const { isTourActive, startTour, closeTour } = useAdminContext();
  const [initialPopup, setInitialPopup] = useState(false);
  const [showNewReleasePopup, setShowNewReleasePopup] = useState(false);

  const closePopup = () => {
    setInitialPopup(false);

    if (profile?.user?.isReleasePopupButtonEnabled) {
      setTimeout(() => {
        setShowNewReleasePopup(true);
      }, 300); // Small delay for smoother UX
    }
  };
  const closeReleasePopup = () => {
    setShowNewReleasePopup(false);
  };
  useEffect(() => {
    if (!isLoading && profile?.user) {
      if (!profile.user?.acceptTerms) {
        setInitialPopup(true);
      } else if (profile?.user?.isReleasePopupButtonEnabled) {
        setShowNewReleasePopup(true);
      }
    }

    // Set user information in Datadog RUM for better tracking
    // if (profile?.user) {
    //   const username = profile.user?.firstName + profile.user?.lastName || '';
    //   datadogRum.setUser({
    //     id: profile?.user?.id || '',
    //     email: profile?.user?.email || '',
    //     name: username
    //   })
    // }
  }, [profile?.user]);

  return (
    <AdminLayout subHeaderTitle="" pageTitle="">
      {initialPopup && (
        <CustomPopUp
          hidePopUp={() => setInitialPopup(false)}
          popUpTitle={"Terms Of Use"}
          hideClose={profile?.user?.acceptTerms === false}
        >
          <TermsAndConditionsPopup
            profileData={profile}
            termsAccepted={profile?.user?.acceptTerms}
            oktaAuth={oktaAuth}
            closePopup={closePopup}
            id=""
          />
        </CustomPopUp>
      )}
      <SecureRoute
        path={match.path}
        render={() => (
          <>
            <PopupWrapper
              type="tour"
              isActive={isTourActive}
              onClose={closeTour}
            />
            <PopupWrapper
              type="release"
              isActive={showNewReleasePopup}
              onClose={closeReleasePopup}
              startTour={startTour}
              isReleasePopupButtonEnabled={
                profile?.user?.isReleasePopupButtonEnabled
              }
            />
          </>
        )}
      />

      <Switch>
        <SecureRoute
          exact
          path={`${match.path}`}
          component={AdminLandingPage}
        />
        <SecureRoute path={`${match.path}/dashboard`} component={Dashboard} />
        <SecureRoute
          path={`${match.path}/test-orders`}
          component={TestOrdersPageRoutes}
        />
        <SecureRoute
          path={`${match.path}/approvals`}
          exact
          component={ApprovalsTableResponsive}
        />
        <SecureRoute
          path={`${match.path}/approvals/review`}
          exact
          component={ApprovalsReviewResponsive}
        />
        <SecureRoute
          path={`${match.path}/approvals/order-authorized`}
          exact
          component={OrderAuthorizedPage}
        />

        <SecureRoute
          path={`${match.path}/settings`}
          component={SettingsPageWithSuspense}
        />
        <SecureRoute
          path={`${match.path}/shipments`}
          component={ShipmentsRoute}
        />
        <SecureRoute
          path={`${match.path}/schedule-pickup`}
          component={() => <SchedulePickup />}
        />
        <SecureRoute
          path={`${match.path}/online-order`}
          component={OrderPageRoutes}
        />
        <SecureRoute
          path={`${match.path}/help`}
          component={HelpPageWithSuspense}
        />
        <SecureRoute
          path={`${match.path}/profile`}
          component={ProfilePageRoutes}
        />
        <SecureRoute
          path={`${match.path}/documents`}
          component={DocumentsPageWithSuspense}
        />
        <SecureRoute
          path={`${match.path}/notifications`}
          component={NotificationsPage}
        />
        <SecureRoute
          path={`${match.path}/patients`}
          component={PatientsPageRoutes}
        />
        <SecureRoute component={NotFoundPage} />
      </Switch>
    </AdminLayout>
  );
}

export default AdminRoutes;
